// Enter Gloves: the admin records a site's new pairs in one pass, sealed.
// Words: GLOVES_ENTER_WORDS.md. Mockup: MOCKUP_GLOVES_ENTER.html (approved 2026-10-24, 6:28 AM, with names shown).
//
// Rules:
// * One row per Active QEW at the site, the same people the Gloves page lists.
// * Every pair goes in sealed. The QEW taps Place In Service from the bag tag when the bag is opened.
// * Same checks and clocks as Gloves Arrived: the test date, Nodes 1 to 3, a new pair only inside the
//   last 30 days of the current one.
// * The row number does the work: a double-tap copies its test date down to the empty open rows below.
// Pure: shared by the page, the server step and the unit tests.

import { GLOVE_SIZES, canReplace, dueOf, testDateProblem, usDate, type GlovePair } from "./gloves";

export type EnterState = "open" | "locked" | "hasPair";

export interface EnterRow {
  qewId: string;
  name: string;
  state: EnterState;
  /** What is on file now, in words. */
  onFile: string;
  /** The size the person record holds, to start the cell with. */
  size: string;
  /** Saving this row retires the current pair as Replaced. */
  replaces: string | null;
}

export interface EnterPerson {
  qewId: string;
  name: string;
  locked: boolean;
  gloveSize: string | null;
  pair: GlovePair | null;
}

const STATUS_WORD: Record<string, string> = { sealed: "sealed", inService: "in service", retired: "retired" };

/** The sheet's rows, in tag order. */
export function enterRows(people: readonly EnterPerson[], today: string): EnterRow[] {
  return [...people]
    .sort((a, b) => a.qewId.localeCompare(b.qewId, "en", { numeric: true }))
    .map((p): EnterRow => {
      const size = p.gloveSize ?? "";
      if (p.locked) return { qewId: p.qewId, name: p.name, state: "locked", onFile: "Opens after Nodes 1 to 3", size, replaces: null };
      if (!p.pair) return { qewId: p.qewId, name: p.name, state: "open", onFile: "No pair", size, replaces: null };
      const pair = p.pair;
      const line = `${pair.pairId.split("-").pop()} · ${STATUS_WORD[pair.status] ?? pair.status} · tested ${usDate(pair.testedOn)} · good until ${usDate(dueOf(pair))}`;
      if (canReplace(pair, today)) return { qewId: p.qewId, name: p.name, state: "open", onFile: `${line} · saving replaces it`, size, replaces: pair.pairId };
      return { qewId: p.qewId, name: p.name, state: "hasPair", onFile: line, size, replaces: null };
    });
}

export interface EnterCell {
  size: string;
  testedOn: string;
}

/** A row is saved once it has both cells. Blank rows are skipped. */
export function isFilled(c: EnterCell | undefined): c is EnterCell {
  return !!c && !!c.size && !!c.testedOn;
}

/** The problem with a filled row, or null. Same words the QEW sees. */
export function enterProblem(c: EnterCell, today: string): string | null {
  if (!(GLOVE_SIZES as readonly string[]).includes(c.size)) return "Size must be one from the list.";
  return testDateProblem(c.testedOn, today);
}

/** "Save 7 Pairs". */
export function saveLabel(n: number): string {
  return `Save ${n} ${n === 1 ? "Pair" : "Pairs"}`;
}

/**
 * Double-tap on a row number: its test date copies down to every open row below with no date yet.
 * Returns the new cells and how many rows took the date.
 */
export function copyDateDown(rows: readonly EnterRow[], cells: Readonly<Record<string, EnterCell>>, from: string): { cells: Record<string, EnterCell>; copied: number } {
  const out: Record<string, EnterCell> = { ...cells };
  const i = rows.findIndex((r) => r.qewId === from);
  const date = cells[from]?.testedOn ?? "";
  if (i < 0 || !date) return { cells: out, copied: 0 };
  let copied = 0;
  for (const r of rows.slice(i + 1)) {
    if (r.state !== "open") continue;
    const c = out[r.qewId] ?? { size: r.size, testedOn: "" };
    if (c.testedOn) continue;
    out[r.qewId] = { ...c, testedOn: date };
    copied++;
  }
  return { cells: out, copied };
}

/** What the server takes: one entry per filled open row. At most 99 per site. */
export interface EnterSend {
  qewId: string;
  gloveSize: string;
  testedOn: string;
}

export function rowsToSend(rows: readonly EnterRow[], cells: Readonly<Record<string, EnterCell>>): EnterSend[] {
  return rows
    .filter((r) => r.state === "open" && isFilled(cells[r.qewId]))
    .map((r) => ({ qewId: r.qewId, gloveSize: cells[r.qewId]!.size, testedOn: cells[r.qewId]!.testedOn }));
}
